import * as THREE from 'three';
import type { HeroDefinition, UnitState } from '../../../shared/game/world-types.js';
import { createCharacterModel } from './character-model.js';
import { shapedHeight } from '../../../content/characters/rigs/humanoid-shape.js';
import { createCharacterAnimator, type CharacterAnimator } from './character-animation.js';
import { HeadMeshBuilder } from './head-mesh-builder.js';
import { loadSoloClips } from './solo-clip-library.js';
import type { HeadDetail } from './human-head.js';

/** Vertical half-extent of the view in meters; wide enough for the tallest hero plus the pedestal. */
const HALF_HEIGHT = 1.22, LOOK_AT = 0.86, TURN = .55;
/** The close-up frames the first hero's head and shoulders. */
const CLOSE_HALF_HEIGHT = .3, CLOSE_LOOK = .93;
/** A plinth is sized to its hero (made for one of `PLINTH_STATURE` metres), so every ancestry is framed alike: from the collar (`PLINTH_BELOW` of the stature) to just over the head (`PLINTH_ABOVE`). Its close-up aims `CLOSE_AIM` from the head joint, which puts the hair near the top and the shoulders below. */
const PLINTH_STATURE = 1.75, PLINTH_BELOW = .115, PLINTH_ABOVE = .057, CLOSE_AIM = -.05;
const FACE = new THREE.Vector3();
/** Radians per dragged pixel, and zoom per wheel pixel (a notch is about 100). */
const SPIN_PER_PIXEL = .012, ZOOM_PER_PIXEL = .0016;
/**
 * `plinth` stands the hero on a worn stone octagon in a bronze collar, with only a hairline of hero colour, instead of the selection pedestal.
 * `handled` lets the viewer turn the hero by dragging and zoom toward the head with the wheel, instead of following the pointer.
 */
export interface PartyStageOptions { plinth?: boolean; handled?: boolean }
interface Figure { group: THREE.Group; rig: ReturnType<typeof createCharacterModel>; animator: CharacterAnimator; state: UnitState; ring: THREE.Mesh<THREE.TorusGeometry, THREE.MeshStandardMaterial>; glow: THREE.Mesh<THREE.CircleGeometry, THREE.MeshBasicMaterial>; chosen: boolean; turn: number; height: number }

/**
 * The idle heroes shown above the party's selection cards: one transparent canvas, one hero per card column.
 * It is a client-only projection of the roster the server offered and holds no game state.
 */
export class PartyStage {
  private readonly renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, .1, 60);
  private figures: Figure[] = [];
  private pointer = 0;
  private visible = false;
  private readonly clock = new THREE.Timer();
  private elapsed = 0;
  /** A brief burst of light on the pedestal, set when the shown hero changes class and decaying to nothing. */
  private flare = 0;
  /** 0 frames the whole row, 1 the first hero's head; eased toward `closeTarget`. */
  private close = 0;
  private closeTarget = 0;
  private readonly headBuilder = new HeadMeshBuilder();
  private showVersion = 0;
  private geometrySignature = '';
  private builtHeights = '';
  private readonly resizeObserver: ResizeObserver;
  private readonly visibilityObserver: IntersectionObserver;
  private readonly movePointer = (event: PointerEvent) => { const box = this.host.getBoundingClientRect(); this.pointer = Math.max(-1, Math.min(1, ((event.clientX - box.left) / Math.max(1, box.width)) * 2 - 1)); };
  private readonly leavePointer = () => { this.pointer = 0; };
  /** The handled figure's turn, and the pointer x where the current drag last was. */
  private spin = 0;
  private dragX?: number;
  private readonly pressPointer = (event: PointerEvent) => { if (event.button !== 0) return; this.dragX = event.clientX; this.host.setPointerCapture(event.pointerId); this.host.classList.add('turning'); };
  private readonly dragPointer = (event: PointerEvent) => { if (this.dragX === undefined) return; this.spin += (event.clientX - this.dragX) * SPIN_PER_PIXEL; this.dragX = event.clientX; };
  private readonly releasePointer = (event: PointerEvent) => { if (this.dragX === undefined) return; this.dragX = undefined; if (this.host.hasPointerCapture(event.pointerId)) this.host.releasePointerCapture(event.pointerId); this.host.classList.remove('turning'); };
  private readonly wheel = (event: WheelEvent) => { event.preventDefault(); const pixels = event.deltaMode === 1 ? event.deltaY * 33 : event.deltaY; this.setZoom(this.closeTarget - pixels * ZOOM_PER_PIXEL); };
  private readonly key = (event: KeyboardEvent) => {
    const step = event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0, zoom = event.key === '+' || event.key === '=' ? 1 : event.key === '-' ? -1 : 0;
    if (step) this.spin += step * Math.PI / 8; else if (zoom) this.setZoom(this.closeTarget + zoom * .2); else if (event.key === 'Home') this.resetView(); else return;
    event.preventDefault();
  };
  private readonly resetView = () => { this.spin = 0; this.setZoom(0); };
  readonly timings = { prepare: 0, assembly: 0, shaders: 0, total: 0 };

  constructor(private host: HTMLElement, private readonly headDetail: HeadDetail = 'high', private readonly options: PartyStageOptions = {}) {
    const { renderer, scene } = this;
    // Idles and stances; a figure holds its procedural pose until they arrive.
    void loadSoloClips();
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2)); renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.shadowMap.enabled = true; renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.domElement.setAttribute('aria-hidden', 'true'); host.append(renderer.domElement);
    scene.add(new THREE.HemisphereLight(0xd5e9ed, 0x3a3029, 2.2));
    const sun = new THREE.DirectionalLight(0xffe0b0, 3.6); sun.position.set(-3, 6, 6); sun.castShadow = true;
    sun.shadow.mapSize.set(1024, 1024); Object.assign(sun.shadow.camera, { left: -12, right: 12, top: 6, bottom: -6 }); sun.shadow.normalBias = .02;
    const rim = new THREE.DirectionalLight(0x89c6dc, 2.6); rim.position.set(4, 4, -6);
    scene.add(sun, rim);
    this.resizeObserver = new ResizeObserver(() => this.resize()); this.resizeObserver.observe(host);
    // Only draw while the party page is on screen; the lobby hides it without telling us.
    this.visibilityObserver = new IntersectionObserver(entries => { this.visible = entries.some(entry => entry.isIntersecting); this.clock.update(); this.sync(); }); this.visibilityObserver.observe(host);
    if (options.handled) {
      host.addEventListener('pointerdown', this.pressPointer); host.addEventListener('pointermove', this.dragPointer); host.addEventListener('pointerup', this.releasePointer); host.addEventListener('pointercancel', this.releasePointer);
      host.addEventListener('wheel', this.wheel, { passive: false }); host.addEventListener('dblclick', this.resetView); host.addEventListener('keydown', this.key);
    } else { host.addEventListener('pointermove', this.movePointer); host.addEventListener('pointerleave', this.leavePointer); }
  }

  /** Replaces the figures with this roster, one column each; `chosen` says which start selected. */
  show(heroes: HeroDefinition[], chosen: ReadonlySet<string>, flare = false) {
    const version = ++this.showVersion;
    const start = performance.now(); this.host.setAttribute('aria-busy', 'true');
    const signature = JSON.stringify(heroes.map(hero => { const { height: _height, ...appearance } = hero.appearance ?? {}; return [hero.id, appearance, hero.color, hero.shield, hero.profile.attacks, hero.gear]; }));
    const heights = JSON.stringify(heroes.map(hero => hero.appearance?.height));
    if (signature === this.geometrySignature && heroes.length === this.figures.length && heroes.every(hero => hero.appearance?.height !== undefined)) {
      // A new height scales the figures at once; it also reshapes the body (`shapeHumanoidBody`), so they are rebuilt behind it.
      this.figures.forEach((figure, index) => { const look = heroes[index].appearance!, height = shapedHeight(look.height!, look.shape); figure.height = figure.rig.height = height; figure.rig.root.scale.setScalar(height / figure.rig.proportions.referenceHeight); figure.chosen = chosen.has(heroes[index].id); });
      this.flare = flare ? 1 : 0; this.resize(); this.sync();
      if (heights === this.builtHeights) {
        this.headBuilder.stop();
        this.timings.prepare = this.timings.assembly = this.timings.shaders = 0; this.timings.total = performance.now() - start;
        this.host.setAttribute('aria-busy', 'false'); if (import.meta.env?.DEV) this.host.dataset.previewTimings = JSON.stringify(this.timings); return;
      }
    }
    if (heroes.length === 1 && typeof Worker !== 'undefined') {
      void this.headBuilder.prepare(heroes[0], this.headDetail).then(ready => {
        this.timings.prepare = performance.now() - start;
        if (ready && version === this.showVersion) void this.showFigures(heroes, chosen, flare, version, start, signature);
      });
      return;
    }
    this.headBuilder.stop();
    this.timings.prepare = performance.now() - start;
    void this.showFigures(heroes, chosen, flare, version, start, signature);
  }

  private async showFigures(heroes: HeroDefinition[], chosen: ReadonlySet<string>, flare: boolean, version: number, start: number, signature: string) {
    const assembly = performance.now();
    const next = heroes.map(definition => {
      const group = new THREE.Group();
      const rig = createCharacterModel(definition, { detail: this.headDetail }); group.add(rig.root);
      const ring = this.options.plinth ? plinth(group, definition.color, rig.height) : pedestal(group, definition.color);
      const glow = new THREE.Mesh(new THREE.CircleGeometry(1.15, 48), new THREE.MeshBasicMaterial({ color: definition.color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }));
      glow.rotation.x = -Math.PI / 2; glow.position.y = -.14; glow.visible = !this.options.plinth; group.add(glow);
      const { maxHp } = definition.profile;
      const state = { id: definition.id, name: definition.name, radius: definition.radius, x: 0, z: 0, route: [],
        health: { hp: maxHp, maxHp, temporaryHp: 0, wounds: 0, maxWounds: 0, dying: false, dead: false },
        combat: { actions: 0, actionRecoveryRemaining: 0, rush: 0, rushDisadvantage: 0, targetId: null, attackId: null, order: 'idle', anticipating: false, opening: false } } as UnitState;
      const animator = createCharacterAnimator(rig, definition);
      return { group, rig, animator, state, ring, glow, chosen: chosen.has(definition.id), turn: 0, height: rig.height };
    });
    this.timings.assembly = performance.now() - assembly;
    const staging = new THREE.Scene(); for (const figure of next) staging.add(figure.group);
    const shaders = performance.now();
    try { await this.renderer.compileAsync(staging, this.camera, this.scene); }
    catch (error) { for (const figure of next) this.releaseFigure(figure); if (version === this.showVersion) this.host.setAttribute('aria-busy', 'false'); console.warn('Character preview shader preparation failed', error); return; }
    this.timings.shaders = performance.now() - shaders;
    if (version !== this.showVersion) { for (const figure of next) this.releaseFigure(figure); return; }
    // Swap only after geometry and GPU programs are ready. The preceding hero remains visible throughout.
    this.clear(); this.figures = next; for (const figure of next) this.scene.add(figure.group);
    this.host.dataset.figures = String(next.length);
    this.geometrySignature = signature; this.builtHeights = JSON.stringify(heroes.map(hero => hero.appearance?.height));
    this.flare = flare ? 1 : 0;
    this.resize(); this.sync();
    this.host.setAttribute('aria-busy', 'false'); this.timings.total = performance.now() - start;
    if (import.meta.env?.DEV) this.host.dataset.previewTimings = JSON.stringify(this.timings);
  }

  /** Frame the first hero's head instead of the whole row. */
  setCloseUp(on: boolean) { this.setZoom(on ? 1 : 0); }
  /** 0 frames the whole row, 1 the first hero's head; values between ease there. */
  setZoom(zoom: number) { this.closeTarget = Math.max(0, Math.min(1, zoom)); if (!this.visible) { this.close = this.closeTarget; this.resize(); } }

  setChosen(index: number, chosen: boolean) {
    const figure = this.figures[index]; if (!figure) return;
    if (chosen && !figure.chosen) this.flare = 1;
    figure.chosen = chosen;
  }

  /** Releases every figure and its GPU resources. */
  dispose() { this.showVersion++; this.headBuilder.stop(); this.resizeObserver.disconnect(); this.visibilityObserver.disconnect(); this.unlisten(); this.clear(); this.renderer.setAnimationLoop(null); this.renderer.dispose(); this.renderer.domElement.remove(); }

  private unlisten() {
    const host = this.host;
    host.removeEventListener('pointermove', this.movePointer); host.removeEventListener('pointerleave', this.leavePointer);
    host.removeEventListener('pointerdown', this.pressPointer); host.removeEventListener('pointermove', this.dragPointer); host.removeEventListener('pointerup', this.releasePointer); host.removeEventListener('pointercancel', this.releasePointer);
    host.removeEventListener('wheel', this.wheel); host.removeEventListener('dblclick', this.resetView); host.removeEventListener('keydown', this.key); host.classList.remove('turning');
  }

  private clear() {
    for (const figure of this.figures) this.releaseFigure(figure);
    this.figures = []; delete this.host.dataset.figures;
  }
  private releaseFigure(figure: Figure) {
      figure.animator.dispose(); figure.rig.skeleton.dispose(); this.scene.remove(figure.group);
      figure.group.traverse(object => {
        if (!(object instanceof THREE.Mesh || object instanceof THREE.Line)) return;
        object.geometry.dispose();
        for (const material of Array.isArray(object.material) ? object.material : [object.material]) material.dispose();
      });
  }

  private sync() { this.renderer.setAnimationLoop(this.visible && this.figures.length ? () => this.frame() : null); }

  /** Fits the view to the host and puts each hero over the middle of its card column. */
  private resize() {
    const width = this.host.clientWidth, height = this.host.clientHeight;
    if (!width || !height) return;
    const aspect = width / height, count = Math.max(1, this.figures.length), halfWidth = HALF_HEIGHT * aspect, step = halfWidth * 2 / count;
    this.figures.forEach((figure, i) => figure.group.position.set(-halfWidth + step * (i + .5), 0, 0));
    // Pedestals stay inside their column however many heroes share the row.
    const scale = Math.min(1, step / 2.1);
    for (const figure of this.figures) figure.group.scale.setScalar(scale);
    this.frameCamera();
    this.renderer.setSize(width, height);
  }
  private frameCamera() {
    const width = this.host.clientWidth, height = this.host.clientHeight, first = this.figures[0];
    if (!width || !height) return;
    const t = first ? this.close * this.close * (3 - 2 * this.close) : 0, aspect = width / height;
    const stature = first ? first.height * first.group.scale.y : 0;
    const wide = this.options.plinth && first ? stature * (1 + PLINTH_BELOW + PLINTH_ABOVE) / 2 : HALF_HEIGHT, look = this.options.plinth && first ? stature * (1 + PLINTH_ABOVE - PLINTH_BELOW) / 2 : LOOK_AT;
    const half = THREE.MathUtils.lerp(wide, CLOSE_HALF_HEIGHT, t);
    const x = first ? first.group.position.x * t : 0, y = THREE.MathUtils.lerp(look, !first ? look : this.options.plinth ? closeAim(first) : first.height * first.group.scale.y * CLOSE_LOOK, t);
    const { camera } = this;
    camera.left = -half * aspect; camera.right = half * aspect; camera.top = half; camera.bottom = -half;
    camera.position.set(x, y + THREE.MathUtils.lerp(2.4, .9, t), 12); camera.lookAt(x, y, 0); camera.updateProjectionMatrix();
  }

  private frame() {
    this.clock.update(); const dt = Math.min(this.clock.getDelta(), .05); this.elapsed += dt;
    this.flare = Math.max(0, this.flare - dt * 1.4);
    if (this.close !== this.closeTarget) {
      this.close += Math.sign(this.closeTarget - this.close) * Math.min(Math.abs(this.closeTarget - this.close), dt * 3);
      this.frameCamera();
    }
    for (const figure of this.figures) {
      const target = figure.chosen ? 1 : 0, material = figure.ring.material;
      figure.turn += ((this.options.handled ? this.spin : this.pointer * TURN) - figure.turn) * Math.min(1, dt * (this.options.handled ? 14 : 4));
      figure.group.rotation.y = figure.turn;
      const pulse = .5 + .5 * Math.sin(this.elapsed * 2.2);
      if (this.options.plinth) material.emissiveIntensity = .35;
      else material.emissiveIntensity += ((figure.chosen ? .55 + .45 * pulse : .08) + this.flare * 1.6 - material.emissiveIntensity) * Math.min(1, dt * 8);
      figure.glow.material.opacity += (target * (.16 + .06 * pulse) + this.flare * .55 - figure.glow.material.opacity) * Math.min(1, dt * 6);
      figure.animator.update(figure.state, figure.state, this.elapsed, undefined, false);
    }
    this.renderer.render(this.scene, this.camera);
  }
}

type Ring = THREE.Mesh<THREE.TorusGeometry, THREE.MeshStandardMaterial>;
/** The selection pedestal: a slate drum ringed in the hero's colour, which pulses while the hero is chosen. */
function pedestal(group: THREE.Group, color: number): Ring {
  const drum = new THREE.Mesh(new THREE.CylinderGeometry(.86, .94, .12, 48), new THREE.MeshStandardMaterial({ color: 0x26353b, roughness: .8, metalness: .25 }));
  drum.position.y = -.07; drum.receiveShadow = true; group.add(drum);
  const ring = new THREE.Mesh(new THREE.TorusGeometry(.85, .018, 6, 64), new THREE.MeshStandardMaterial({ color, emissive: color, metalness: .6, roughness: .4 }));
  ring.rotation.x = -Math.PI / 2; group.add(ring);
  return ring;
}
/** A worn stone octagon in a riveted bronze collar, the HUD medallions' make, with a hairline of the hero's colour round its top. */
function plinth(parent: THREE.Group, color: number, stature: number): Ring {
  const group = new THREE.Group(); group.scale.setScalar(stature / PLINTH_STATURE); parent.add(group);
  const stone = new THREE.Mesh(new THREE.CylinderGeometry(.44, .47, .1, 8), new THREE.MeshStandardMaterial({ color: 0x3b3631, roughness: .95, flatShading: true }));
  stone.position.y = -.05; stone.receiveShadow = true; group.add(stone);
  const collar = new THREE.Mesh(new THREE.CylinderGeometry(.5, .54, .08, 8), new THREE.MeshStandardMaterial({ color: 0x5e3b1f, metalness: .65, roughness: .45, flatShading: true }));
  collar.position.y = -.12; collar.receiveShadow = true; group.add(collar);
  const rivet = new THREE.SphereGeometry(.016, 8, 6), brass = new THREE.MeshStandardMaterial({ color: 0xb08a4a, metalness: .85, roughness: .3 });
  // Octagon sides face the half-steps between vertices; their apothem is the collar's radius times cos(22.5°).
  for (let side = 0; side < 8; side++) {
    const angle = (side + .5) * Math.PI / 4, reach = .52 * Math.cos(Math.PI / 8) + .004;
    const head = new THREE.Mesh(rivet, brass); head.position.set(Math.sin(angle) * reach, -.12, Math.cos(angle) * reach); group.add(head);
  }
  // A torus of eight segments is an octagon with its corners on the cylinder's, so the hairline follows the stone's edge.
  const ring = new THREE.Mesh(new THREE.TorusGeometry(.43, .006, 4, 8), new THREE.MeshStandardMaterial({ color, emissive: color, metalness: .6, roughness: .4 }));
  ring.rotation.x = -Math.PI / 2; ring.position.y = .002; group.add(ring);
  return ring;
}
/** Where the close-up looks, from the figure's head joint, so it frames every ancestry's head alike. */
function closeAim(figure: Figure) {
  figure.group.updateWorldMatrix(true, true);
  return figure.rig.head.getWorldPosition(FACE).y + CLOSE_AIM * figure.group.scale.y;
}
